import * as React from "react";

import { HtmlBox } from "@/components/ui/html-box";
import { IconButton } from "@/components/ui/icon-button";
import { MuiArrowDropDownIcon } from "@/components/ui/mui-arrow-icons";
import { MuiArrowDropUpIcon } from "@/components/ui/mui-spec-icons";

// The spec-level Specification Notes block — the donor's `SpecificationNotes`
// (`shared/components/SpecificationNotes.tsx`), the frame's part beneath the
// metadata line. The line-level notes with their More/Less are the lines
// body's (#695), a different component in the source too.
//
//   - absent when the spec has no notes;
//   - a column, gap 8, on `#F5F5F5`, padded `8px 16px`, 8px above and below
//     (48px tall closed, measured on the running donor, #694);
//   - a header row, gap 4: a small `IconButton` at `padding: 4px` around the
//     default 24px arrow, `aria-label="expand notes"` and no `aria-expanded`
//     (the donor's is static), then the subtitle1 "Specification Notes" at
//     weight 600 — an `h6`, `subtitle1`'s default element;
//   - **collapsed on arrival**, the arrow pointing down; a press shows the
//     body and turns it up. (The donor's state is named `isCollapsed` and
//     means the opposite; the observable start is what is carried.)
//   - the body is the notes' HTML with `word-break: break-word`, 8px above,
//     and the first of each element type's margin zeroed (`:first-of-type`,
//     so a first list after a paragraph loses its margin too). The notes
//     arrive sanitized on read (ADR-0012), which the donor's never were.

export function SpecificationNotes({ notes }: { notes: string | null }) {
  const [open, setOpen] = React.useState(false);
  if (!notes) return null;
  return (
    <div
      data-testid="specification-notes"
      className="bg-mui-grey-100 my-2 flex flex-col gap-2 px-4 py-2"
    >
      <div className="flex flex-row items-center gap-1">
        <IconButton
          size="small"
          aria-label="expand notes"
          className="p-1"
          onClick={() => setOpen((was) => !was)}
        >
          {open ? <MuiArrowDropUpIcon /> : <MuiArrowDropDownIcon />}
        </IconButton>
        <h6 className="m-0 text-base leading-normal font-semibold">Specification Notes</h6>
      </div>
      {open ? <HtmlBox html={notes} className="mt-2 break-words [&>:first-of-type]:m-0" /> : null}
    </div>
  );
}
